import { ReactNode, useState, useMemo, useEffect } from "react";
import { Box, Typography, Stack } from "@mui/material";
import { getValueAtPath, toolbarSearchSx } from "utils";
import { Column } from "types";
import { MenuOption } from "components/buttons/MoreOptionsButton";
import AutoSizer from "react-virtualized-auto-sizer";
import MobileListSearchBar from "components/layout/MobileListSearchBar";
import { ComponentType } from "react";
import { useTranslation } from "react-i18next";
import {
  FixedSizeList as _FixedSizeList,
  FixedSizeListProps,
  ListChildComponentProps,
} from "react-window";
import { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers";
import { useSearchParams } from "react-router";

const FixedSizeList =
  _FixedSizeList as unknown as ComponentType<FixedSizeListProps>;
export interface MobilePanelListProps<T extends object> {
  renderItem: (item: T, index: number) => ReactNode;
  itemHeight: number;
  columns: Column[];
  title: string;
  data: T[];
  menuOptions?: MenuOption[];
  withSearchBar?: boolean;
  startDate?: Dayjs | null;
  endDate?: Dayjs | null;
  setStartDate?: (date: Dayjs | null) => void;
  setEndDate?: (date: Dayjs | null) => void;
  heightOffset?: number;
}

const MobilePanelList = <T extends object>({
  renderItem,
  columns,
  data,
  menuOptions,
  itemHeight,
  withSearchBar = false,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  heightOffset = 0,
}: MobilePanelListProps<T>): ReactNode => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const filteredData: T[] = useMemo(() => {
    if (searchQuery === "") {
      return data;
    }

    return data.filter((item: T) => {
      let pass = false;
      for (const column of columns) {
        if (column.searchKey) {
          const value = getValueAtPath(item, column.searchKey);
          if (
            value &&
            value.toString().toLowerCase().includes(searchQuery.toLowerCase())
          ) {
            pass = true;
          }
        }
      }
      return pass;
    });
  }, [searchQuery, columns, data]);

  const enableDateSearch = !!(setStartDate && setEndDate);

  // search bar height is 60px
  // date bar height is 60
  // tab nav height is 50px
  // mobile nav bar height is 100px

  const topMargin = useMemo(() => {
    let m = 0;
    if (withSearchBar) {
      m += 60;
    }
    if (enableDateSearch) {
      m += 60;
    }
    return m;
  }, [withSearchBar, enableDateSearch]);

  return (
    <Box
      sx={{
        borderRadius: 0,
        backgroundColor: "white",
        mt: `${topMargin}px`,
        height: `calc(100dvh - 150px - ${topMargin}px - ${heightOffset}px)`,
      }}
    >
      {withSearchBar && (
        <MobileListSearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          menuOptions={menuOptions}
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          heightOffset={heightOffset}
        />
      )}
      <AutoSizer>
        {({ width, height }) => (
          <FixedSizeList
            width={width}
            height={height}
            itemCount={filteredData.length}
            itemSize={itemHeight}
            itemData={filteredData}
          >
            {(props: ListChildComponentProps<T[]>) => (
              <div style={props.style}>
                {renderItem(props.data[props.index], props.index)}
              </div>
            )}
          </FixedSizeList>
        )}
      </AutoSizer>
    </Box>
  );
};

export default MobilePanelList;
